import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Calendar, 
  ExternalLink, 
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionManagement = ({ userEmail }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!userEmail) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/api/payments/subscription-status/${encodeURIComponent(userEmail)}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch subscription status');
        }
        
        const data = await response.json();
        setSubscription(data);
      } catch (err) {
        console.error('Error fetching subscription:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [userEmail]);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/payments/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer_email: userEmail,
          return_url: window.location.href
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to open billing portal');
      }
      
      const data = await response.json();
      window.location.href = data.url;
    } catch (err) {
      console.error('Error opening portal:', err);
      setError(err.message);
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
        </div>
      </div>
    );
  }

  // No subscription found
  if (!subscription?.has_subscription) {
    return null; // Don't show section if no subscription
  }

  const activeSub = subscription.active_subscription;
  const isTrialing = activeSub?.in_trial;
  const willCancel = activeSub?.cancel_at_period_end;
  
  // Format dates
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Status badge
  const getStatusBadge = () => {
    if (willCancel) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/10 text-yellow-400 text-xs font-medium rounded-full">
          <AlertCircle className="w-3 h-3" />
          Cancelling
        </span>
      );
    }
    if (isTrialing) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 text-blue-400 text-xs font-medium rounded-full">
          <Clock className="w-3 h-3" />
          Trial
        </span>
      );
    }
    if (activeSub?.status === 'active') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/10 text-green-400 text-xs font-medium rounded-full">
          <CheckCircle className="w-3 h-3" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-500/10 text-zinc-400 text-xs font-medium rounded-full">
        {activeSub?.status || 'Unknown'}
      </span>
    );
  };

  return (
    <section 
      className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
      data-testid="subscription-management"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-emerald-500" />
          Your Subscription
        </h2>
        {getStatusBadge()}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {/* Plan Info */}
        <div className="p-4 bg-zinc-800/50 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="text-zinc-400 text-sm">Plan</span>
            <span className="text-white font-medium">
              {activeSub?.plan_name || 'Companion'}
            </span>
          </div>
          
          {activeSub?.amount && (
            <div className="flex items-center justify-between mb-3">
              <span className="text-zinc-400 text-sm">Price</span>
              <span className="text-white">
                £{activeSub.amount.toFixed(2)}/{activeSub.interval === 'year' ? 'year' : 'month'}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-zinc-400 text-sm flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {willCancel ? 'Access until' : isTrialing ? 'Trial ends' : 'Next billing'}
            </span>
            <span className={`font-medium ${willCancel ? 'text-yellow-400' : 'text-white'}`}>
              {formatDate(isTrialing ? activeSub?.trial_end : activeSub?.current_period_end)}
            </span>
          </div>
        </div>

        {/* Cancel Warning */}
        {willCancel && (
          <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <p className="text-yellow-400 text-sm">
              Your subscription is set to cancel. You'll have access until {formatDate(activeSub?.current_period_end)}.
            </p>
          </div>
        )}

        {/* Manage Button */}
        <button
          onClick={openBillingPortal}
          disabled={portalLoading}
          className="w-full flex items-center justify-center gap-2 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          data-testid="manage-billing-button"
        >
          {portalLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Opening...
            </>
          ) : (
            <>
              Manage Billing
              <ExternalLink className="w-4 h-4" />
            </>
          )}
        </button>
        
        <p className="text-zinc-500 text-xs text-center">
          Update payment method, view invoices, or cancel subscription
        </p>
      </div>
    </section>
  );
};

export default SubscriptionManagement;
